import { Component, Input } from "@angular/core";
import { IVTableRow } from "@app/directives/_layout/v-table/_models/IVTableRow";
import { IBreadcrumb } from "@app/directives/_misc/breadcrumbs/breadcrumbs.component";
import { IFilterOptions } from "@app/interfaces/filters/IFilterOptions";
import { IDriver } from "@app/interfaces/IDriver";
import { IVehicle } from "@app/interfaces/IVehicle";
import { ReportTableRowDataSource } from "@app/main/components/reports/enums/ReportTableRowDataSource";
import { IColumnHeader } from "@app/main/components/reports/models/IColumnHeader";
import { IReportDefinition } from "@app/main/components/reports/models/IReportDefinition";
import { IReportParameters, ReportParameterType } from "@app/main/components/reports/models/IReportParameters";
import { IReportRow } from "@app/main/components/reports/models/IReportRow";
import { CameraDisplayService } from "@app/main/components/videos/cameraDisplay.service";
import { IDashboardWidget } from "@app/main/modules/dashboard/_models/IDashboardWidget";
import { IDrillDownReport } from "@app/main/modules/dashboard/_models/IDrillDownReport";
import { DashboardService } from "@app/main/modules/dashboard/dashboard.service";
import { DateService } from "@app/services/date.service";
import { DriverService } from "@app/services/driver.service";
import { FilterOptionsService } from "@app/services/filterOptions.service";
import { TranslationService } from "@app/services/translation.service";
import { VehicleService } from "@app/services/vehicle.service";
import copy from "fast-copy";
import { DynamicReportService } from "../../_services/dynamicReports.service";
import './drill-down-report-table.component.scss';

@Component({
    selector: 'v-drill-down-report-table',
    template: require('./drill-down-report-table.component.html')
})
export class VDrillDownReportTableComponent {
    @Input('report') initialReport: IDrillDownReport;
    @Input() initialDrillRow: IReportRow;
    @Input() filterOptions: IFilterOptions;
    @Input() startDate: Date;
    @Input() endDate: Date;
    @Input() title: string;

    //--

    allColumns: IColumnHeader[];
    vehicles: IVehicle[];
    drivers: IDriver[];

    reportList: IDrillDownReport[];
    displayReport: IDrillDownReport;


    loading: boolean;
    breadcrumbs: IBreadcrumb<ReportTableRowDataSource>[];
    chartWidgetConfig: IDashboardWidget;
    reportBreadcrumbs: any;

    filters: IFilterOptions;
    showColumnEditor: boolean;

    constructor(
        private readonly cameraDisplayService: CameraDisplayService,
        private readonly dateService: DateService,
        private readonly dashboardService: DashboardService,
        private readonly dynamicReportService: DynamicReportService,
        private readonly filterOptionsService: FilterOptionsService,
        private readonly vehicleService: VehicleService,
        private readonly driverService: DriverService,
        private readonly translationService: TranslationService
    ) {
    }

    ngOnInit(): void {
        this.loadRelatedData();
    }

    ngOnChanges(): void {
        this.reportList = [];

        if (this.initialReport) {
            // Create a copy to prevent us making changes to any original
            this.initialReport = copy(this.initialReport);

            this.initialReport.Title = this.initialReport.Title || this.translationService.t("Common.Report");
            if (!this.title) {
                this.title = this.initialReport.Title;
            }

            this.setReportClickable(this.initialReport);

            // If we have no filter options create them from the report parameters
            if (!this.filterOptions) {
                this.filterOptions = { dateFilter: { start: this.startDate, end: this.endDate } };
            }


            if (!this.initialReport.Parameters) {
                // TEMP.. maybe
                this.initialReport.Parameters = {
                    Start: this.filterOptions.dateFilter.start,
                    End: this.filterOptions.dateFilter.end
                }
            }
            else {
                this.initialReport.Parameters.Start = this.filterOptions.dateFilter.start
                this.initialReport.Parameters.End = this.filterOptions.dateFilter.end;
            }

            // If we have no data, auto collect
            if (!this.initialReport.TableDefinition.Rows || this.initialReport.TableDefinition.Rows.length == 0) {
                this.loadReportData(this.initialReport).then(report => {

                    this.selectReport(report);
                });
            }
            else {
                this.selectReport(this.initialReport);
            }

            setTimeout(() => {
                this.updateChartData();
            }, 300);
        }
    }

    rowDataSourceChanged(report: IDrillDownReport, $row: ReportTableRowDataSource, $subRow: ReportTableRowDataSource) {
        report.RowDataSource = $row;

        this.loadReportData(report);
    }

    onBreadcrumbClick(event: any) {
        this.selectReport(this.reportList[event.index]);
    }

    onRowClicked(row: IVTableRow, $event: MouseEvent) {
        if (this.performCustomClickAction(this.displayReport, row, $event)) {
            return;
        }

        this.drillDown(this.displayReport, row);
    }

    onRowsUpdated(rows: IVTableRow[]) {    
        if (this.initialDrillRow) {
            const tableRow = rows.find(row => row.data === this.initialDrillRow);
            if (tableRow) {
                this.drillDown(this.displayReport, tableRow);
                this.initialDrillRow = null;
            }
        }
    }

    toggleShowColumnEditor(forceState?: boolean) {
        this.showColumnEditor = !this.showColumnEditor;

        if (forceState !== undefined) {
            this.showColumnEditor = forceState;
        }

        if (!this.showColumnEditor) {
            const reportCopy = copy(this.displayReport);
            this.addReportToList(reportCopy);

            this.loadReportData(reportCopy).then(() => {
                this.selectReport(reportCopy);
            });
        }
    }

    selectedHeadersChanged(columns: IColumnHeader[]) {
        this.displayReport.TableDefinition.Headers = columns;
    }

    back() {
        const previousReport = this.reportList[this.reportList.length - 2];
        const reportCopy = this.createReportCopy(previousReport);

        this.reportList[this.reportList.length - 2] = reportCopy;

        this.selectReport(reportCopy);
        this.renderReport();
    }

    showCustomView = ReportTableRowDataSource.None;

    toggleCustomView(rowDataSource: ReportTableRowDataSource) {
        if (this.showCustomView == rowDataSource) {
            this.showCustomView = ReportTableRowDataSource.None;
            return;
        }

        this.showCustomView = rowDataSource;
    }
    
    //--

    private loadRelatedData() {

        const columnPromise = this.dynamicReportService.getDisplayColumnHeaders().then(headers => { this.allColumns = headers; }) as Promise<IColumnHeader[]>;
        const vehiclesPromise = this.vehicleService.getVehicles({ includePeripheralMappings: true }).then(vehicles => { this.vehicles = vehicles }) as Promise<IVehicle[]>;
        const driversPromise = this.driverService.getDrivers().then(drivers => { this.drivers = drivers }) as Promise<IDriver[]>;

        Promise.all([columnPromise, vehiclesPromise, driversPromise]).finally(() => {
        });
    }

    private setReportClickable(report: IDrillDownReport) {
        if (report.SubRowDataSource) {
            report.clickable = this.dataSourceSupportsClicking(report.SubRowDataSource);
        }
        else {
            report.clickable = this.dataSourceSupportsClicking(report.RowDataSource);
        }

    }

    private dataSourceSupportsClicking(dataSource: ReportTableRowDataSource): boolean {

        switch (dataSource) {
            case ReportTableRowDataSource.Journey:
            case ReportTableRowDataSource.Route:
            case ReportTableRowDataSource.CommonEvent:
            case ReportTableRowDataSource.GeofenceVisits:
            case ReportTableRowDataSource.None:
                return false;
        }

        return true;
    }

    private loadReportData(report: IDrillDownReport): Promise<IDrillDownReport> {
        report.TableDefinition.Rows = [];

        report.Parameters.CustomParameters = [
            {
                ParameterType: ReportParameterType.InclusiveGroupFiltering,
                Active: true,
                Value: true
            },
            {
                ParameterType: ReportParameterType.ExcludeGpsJourneys,
                Active: true,
                Value: true
            }
        ];

        const promises: Promise<any>[] = [];

        promises.push(this.dashboardService.populateDataDefinition(report));
        promises.push(new Promise((res) => setTimeout(() => { res(null) }, 500))); // This makes it 'feel' nicer

        this.loading = true;

        return Promise.all(promises)
            .then(([results]) => {

                report.TableDefinition.Rows = results.TableDefinition.Rows;
                report.Parameters = results.Parameters;

                // If we have no sub row data source, set to be most likely so we can understand the data type
                if (report.SubRowDataSource === null) {
                    report.SubRowDataSource = this.getNextDrillDownDataSource(report);
                }

                return report;
            })
            .finally(() => {
                this.loading = false;
                this.updateChartData();
            });
    }

    private selectReport(report: IDrillDownReport) {

        this.displayReport = report;

        if (!this.title) {
            this.title = this.initialReport.Title;
        }

        // Clear out any reports (and thus breadcrumbs) deeper than myself
        const index = this.reportList.indexOf(report);
        this.reportList.splice(index + 1);
        this.addReportToList(this.initialReport/*, false*/);

        this.createBreadcrumbs();
        this.createBreadCrumbsFromReportParameters(report.Parameters);
        this.filters = this.filterOptionsService.createFilterOptions(report);
    }

    private updateChartData() {
        if (!this.displayReport || !this.chartWidgetConfig) {
            return;
        }

        this.chartWidgetConfig.dataDefinition = this.displayReport;
        this.chartWidgetConfig.onDataDefinitionUpdated();
    }

    private getNextDrillDownDataSource(report: IDrillDownReport): ReportTableRowDataSource {

        switch (report.RowDataSource) {
            case ReportTableRowDataSource.Week:
                return ReportTableRowDataSource.Day;

            case ReportTableRowDataSource.Day:
            case ReportTableRowDataSource.VehicleGroup:
                return ReportTableRowDataSource.Vehicle;

            case ReportTableRowDataSource.DriverGroup:
                return ReportTableRowDataSource.Driver;

            case ReportTableRowDataSource.Vehicle:
            case ReportTableRowDataSource.Driver:
                return this.determineNextMostLikeleyDataSource(report);

            default:
                return ReportTableRowDataSource.None;
        }
    }

    private addReportToList(report: IDrillDownReport, generateReportTitle = true) {

        // Check to see if we have this report in here already, based on our data source
        const index = this.reportList.findIndex(r => r.RowDataSource == report.RowDataSource);
        if (index >= 0) {
            this.reportList[index] = report;
        }
        else {
            this.reportList.push(report);
        }

        this.createBreadcrumbs();
    }

    private createBreadcrumbs() {
        this.breadcrumbs = this.reportList.map(r => { return { title: r.Title } });
    }

    private createBreadCrumbsFromReportParameters(reportParameters: IReportParameters) {

        this.reportBreadcrumbs = [];

        if (reportParameters.VehicleId) {
            const vehicle = this.vehicleService.findVehicle(this.vehicles, reportParameters.VehicleId);
            if (vehicle) {
                this.reportBreadcrumbs.push({ title: `Vehicle: ${vehicle.ShortReg}` });
            }
        }
        else if (reportParameters.VehicleGroupId) {
            this.reportBreadcrumbs.push({ title: "Vehicles in Group X" });
        }
        else if (reportParameters.DriverId) {
            const driver = this.driverService.findDriver(this.drivers, reportParameters.DriverId);
            if (driver) {
                this.reportBreadcrumbs.push({ title: `Driver: ${driver.DisplayName}` });
            }
        }
        else if (reportParameters.DriverGroupId) {
            this.reportBreadcrumbs.push({ title: "Drivers in Group X" });
        }
        else {
            this.reportBreadcrumbs.push({ title: "All Vehicles and Drivers" });
        }

        if (this.dateService.isSameDate(reportParameters.Start, reportParameters.End)) {
            this.reportBreadcrumbs.push({ title: this.dateService.formatDate(reportParameters.Start) });
        }
        else {
            const startDate = this.dateService.formatDate(reportParameters.Start);
            const endDate = this.dateService.formatDate(reportParameters.End);

            this.reportBreadcrumbs.push({ title: `${startDate} to ${endDate}` });
        }
    }

    private determineNextMostLikeleyDataSource(report: IDrillDownReport): ReportTableRowDataSource {

        if (report.SubRowDataSource) {
            return report.SubRowDataSource;
        }

        let reportType = ReportTableRowDataSource.None

        let index = 0

        while (reportType === ReportTableRowDataSource.None && index < report.TableDefinition.Headers.length) {
            const header = report.TableDefinition.Headers[index];
            if (ReportTableRowDataSource[header.ReportType]) {
                reportType = ReportTableRowDataSource[header.ReportType];
                break;
            }

            switch (header.ReportType) {
                case "Tms":
                    reportType = ReportTableRowDataSource.Route;
                    break;
                case "Camera":
                    reportType = ReportTableRowDataSource.CameraEvent;
                    break;
                case "Alcolock":
                    reportType = ReportTableRowDataSource.AlcolockRecord;
                    break;
                case "CommonEvent":
                    reportType = ReportTableRowDataSource.CommonEvent;
                    break;
                case "StationaryLocation":
                    reportType = ReportTableRowDataSource.StationaryLocations;
                    break;
                case "Tacho":
                    reportType = ReportTableRowDataSource.TachoRecord;
                    break;
                case "Temperature":
                    reportType = ReportTableRowDataSource.TemperatureRecord;
                    break;
                case "TemperatureOutOfBounds":
                    reportType = ReportTableRowDataSource.TemperatureOutOfBoundsRecord;
                    break;
                case "VehicleActivity":
                    //// Maybe needs to be something else?
                    reportType = ReportTableRowDataSource.Journey;
                    break;
            }

            index++;
        }

        return reportType;
    }

    private performCustomClickAction(report: IDrillDownReport, row: IVTableRow, $event: MouseEvent): boolean {
        if (!row?.data?.Parameters?.PrimaryReportParameter) {
            return false;
        }

        switch (row.data.Parameters.PrimaryReportParameter.ParameterType) {
            case ReportParameterType.CameraEventId:
                this.cameraDisplayService.showVideoPlayerById(row.data.Parameters.PrimaryReportParameter.Value, $event);
                return true;

            case ReportParameterType.GeofenceVisitId:
                return true;
        }

        return false;
    }

    private drillDown(report: IReportDefinition, row: IVTableRow) {
        const reportCopy: IDrillDownReport = copy(report);

        this.setNextParameters(reportCopy, row);
        this.setNextDrillDownDataSource(reportCopy);

        if (reportCopy.RowDataSource == ReportTableRowDataSource.Journey) {
            // Journeys do not have a title column, so remove it and replace with Start and End time
            reportCopy.TableDefinition.Headers.splice(0, 1);
            reportCopy.TableDefinition.Headers.unshift(this.dynamicReportService.findColumn("EndLocation", "Journey", this.allColumns));
            reportCopy.TableDefinition.Headers.unshift(this.dynamicReportService.findColumn("JourneyEnd", "Journey", this.allColumns));
            reportCopy.TableDefinition.Headers.unshift(this.dynamicReportService.findColumn("StartLocation", "Journey", this.allColumns));
            reportCopy.TableDefinition.Headers.unshift(this.dynamicReportService.findColumn("JourneyStart", "Journey", this.allColumns));
            reportCopy.TableDefinition.Headers.unshift(this.dynamicReportService.findColumn("RowTitle", "SharedReport", this.allColumns));
        }

        reportCopy.TableDefinition.Rows = [];

        this.addReportToList(reportCopy);
        this.loadReportData(reportCopy).then(() => {

            this.selectReport(reportCopy);
        });
    }

    private setNextParameters(report: IDrillDownReport, row: IVTableRow): void {
        report.Parameters = row.data.Parameters;
    }

    private setNextDrillDownDataSource(report: IDrillDownReport): void {

        report.RowDataSource = this.getNextDrillDownDataSource(report);

        this.setReportClickable(report);
    }

    private createReportCopy(report: IDrillDownReport): IDrillDownReport {
        const reportCopy = copy(report);
        this.addUniqueId(reportCopy);

        if (reportCopy.TableDefinition?.Rows) {
            reportCopy.TableDefinition.Rows.forEach(row => {
                this.addUniqueId(row);

                if (row.Cells) {
                    row.Cells.forEach(cell => {
                        this.addUniqueId(cell);
                    });
                }
            });
        }
        return reportCopy;
    }

    private addUniqueId(obj: any): void {
        obj._backNavId = `nav_${Date.now()}_${Math.random()}`;
    }

    private renderReport() {
        window.requestAnimationFrame(() => {
            this.displayReport = { ...this.displayReport };
        });
    }
}
